import type { StoredOp } from "@envstash/protocol";
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export interface DeviceRecord {
  id: "current";
  /** Server-assigned device id for the most recent account on this browser. */
  deviceId: string | null;
  /** Raw 32-byte X25519 public key. */
  publicKey: Uint8Array;
  /** Non-extractable WebCrypto key (preferred) ... */
  cryptoKey: CryptoKey | null;
  /** ... or a software secret when WebCrypto X25519 is unavailable. */
  softwareSecret: Uint8Array | null;
  createdAt: number;
}

interface EnvStashDB extends DBSchema {
  device: { key: string; value: DeviceRecord };
  ops: { key: [string, number]; value: StoredOp & { vault_id: string }; indexes: { by_vault: string } };
  meta: { key: string; value: unknown };
}

let dbPromise: Promise<IDBPDatabase<EnvStashDB>> | null = null;

export function db(): Promise<IDBPDatabase<EnvStashDB>> {
  dbPromise ??= openDB<EnvStashDB>("envstash", 1, {
    upgrade(d) {
      d.createObjectStore("device", { keyPath: "id" });
      const ops = d.createObjectStore("ops", { keyPath: ["vault_id", "seq"] });
      ops.createIndex("by_vault", "vault_id");
      d.createObjectStore("meta");
    },
  });
  return dbPromise;
}

export async function loadCachedOps(vaultId: string): Promise<StoredOp[]> {
  const d = await db();
  const rows = await d.getAllFromIndex("ops", "by_vault", vaultId);
  return rows.map(({ vault_id: _v, ...op }) => op).sort((a, b) => a.seq - b.seq);
}

export async function cacheOps(vaultId: string, ops: StoredOp[]): Promise<void> {
  if (ops.length === 0) return;
  const d = await db();
  const tx = d.transaction("ops", "readwrite");
  await Promise.all([...ops.map((op) => tx.store.put({ ...op, vault_id: vaultId })), tx.done]);
}

export async function clearCachedOps(vaultId: string): Promise<void> {
  const d = await db();
  const tx = d.transaction("ops", "readwrite");
  let cursor = await tx.store.index("by_vault").openCursor(vaultId);
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await (await db()).get("meta", key)) as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put("meta", value, key);
}
